import type React from 'react';

import { Link, Text } from '@change/ds-core-ui';
import { ArrowLeftIcon } from '@change/ds-icons';

import { Translate, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/petitionUpdate/details.yml';

import { usePetitionUpdatePageData } from '#src/app/pages/petitionUpdate/details/pageContext';
import { useLoginState } from '#src/app/shared/hooks/session';

const BackToDashboardBase: React.FC<EmptyProps> = () => {
	const {
		data: { update, isAiPostUpdateEnabled },
	} = usePetitionUpdatePageData();
	const loginState = useLoginState();

	// Only show the back-to-dashboard navigation to the petition owner (anyone who can
	// edit the update). Petition update pages are public, so this stays hidden for
	// visitors who have no dashboard to return to. It's also gated behind the AI post
	// update experiment, since that's the dashboard feature this link sends starters back to.
	const canEdit = loginState !== 'GUEST' && update.petition.viewerCanEdit;
	if (!isAiPostUpdateEnabled || !canEdit) return null;

	// Return to the "update supporters" section of the petition dashboard, where the
	// starter came from when posting the update.
	const dashboardUrl = `/p/${encodeURIComponent(update.petition.slug)}/dashboard/updates`;

	return (
		<header className="border-stroke-weaker bg-background-base w-full border-b" data-qa="back-to-dashboard">
			<div className="container flex h-[60px] items-center">
				<Link href={dashboardUrl} variant="secondary" className="w-fit" data-qa="back-to-dashboard-link">
					<span className="flex items-center gap-3">
						<ArrowLeftIcon className="text-icon-strong" size="medium" aria-hidden />
						<Text variant="body" fontWeight="bold">
							<Translate value="corgi.petition.petition_update.details.back_to_dashboard" />
						</Text>
					</span>
				</Link>
			</div>
		</header>
	);
};

export const BackToDashboard = withI18nPlaceholders(I18N_PLACEHOLDERS, BackToDashboardBase);
